import type { NavItem } from '@realtyfeed/app-shell';
import { ENDPOINT_PATHS } from '@realtyfeed/auth';
import { ROUTES } from '@realtyfeed/routes';
import {
  BuildingsIcon,
  CardIcon,
  ChartSquareIcon,
  DocumentsIcon,
  InboxLetterIcon,
  MessagesIcon,
  ProfileTickIcon,
  SearchIcon,
  SettingsGearIcon,
  UsersIcon,
} from '@realtyfeed/ui';

/**
 * The sidebar.
 *
 * Ported from the legacy `src/configs/navigation/vertical/index.tsx` — the eleven sections a
 * staff member actually sees, with the entries that were commented out left out.
 *
 * ### `apiPath` is not decoration
 *
 * Each entry lists the endpoint paths its screen calls; the shell hides the entry unless the
 * session may use at least one of them (`filterNavByPermission`). **An entry without one is
 * visible to everyone.** `UNRESTRICTED_NAV_IDS` below is the list of entries where that is
 * deliberate, and `navigation.test.ts` fails if any other entry omits it.
 *
 * ### Two departures from the legacy config, both deliberate
 *
 * 1. **No `LogOut` entry.** The legacy sidebar carried one; the shell's user menu already has
 *    Log out, and a second copy in the sidebar is the Vuexy template showing through. Nothing
 *    is lost — see `UserMenu`.
 * 2. **Icons are the design system's, not the legacy SVG imports.** The legacy config
 *    `require()`d a light and dark PNG/SVG pair per section (`icon` / `icon_l`) because its
 *    theme could not recolour them. The `ds-*` icons here take `currentColor`, so one icon
 *    serves both themes.
 */
export const navigation: NavItem[] = [
  {
    id: 'customers',
    title: 'Customers',
    icon: UsersIcon,
    children: [
      {
        id: 'customers',
        title: 'Customers',
        to: ROUTES.customers,
        // Customer Analytics has no entry of its own in either app and sits outside
        // `/customer-list`, so before 5H.5 it was a customer-data screen with no gate at all.
        //
        // One entry for both datasets: the screen has a Customers / MLS Customers tab strip, so
        // the MLS list and its detail screens keep this entry lit, and either permission shows it.
        matchPaths: [ROUTES.customerAnalytics, ROUTES.mlsCustomers],
        apiPath: [
          ENDPOINT_PATHS.AdminCustomerManagementCustomersGet,
          ENDPOINT_PATHS.AdminCustomerManagementMlsCustomerGet,
        ],
      },
      {
        id: 'requests',
        title: 'MLS Requests',
        to: ROUTES.mlsRequests,
        apiPath: [ENDPOINT_PATHS.ProvidersMlsMlsAccessCustomerRequestsMlsAccessCustomerRequestGet],
      },
      {
        id: 'apiKeyMgmt',
        title: 'API Key Mgmt',
        to: ROUTES.apiKeys,
        apiPath: [ENDPOINT_PATHS.AdminApiKeyManagerListPost],
      },
    ],
  },

  {
    id: 'salesMarketing',
    title: 'Sales & Marketing',
    icon: CardIcon,
    children: [
      {
        id: 'packages',
        title: 'Packages',
        to: ROUTES.packages,
        apiPath: [ENDPOINT_PATHS.MembershipAdminPackagesV2Get],
      },
      {
        id: 'usagePlans',
        title: 'Usage Plans',
        to: ROUTES.usagePlans,
        // The editors do not sit under `/usage-plan`. Without this the sidebar goes dark on
        // them, and — since 5H.5 — the route inherits no permission either.
        matchPaths: [ROUTES.addUsagePlan, '/edit-usage-plan'],
        apiPath: [ENDPOINT_PATHS.AdminUsagePlansGet],
      },
      {
        id: 'resources',
        title: 'Resources',
        to: ROUTES.resources,
        // A usage plan's own resource list. Its legacy nav entry is commented out and nothing
        // links to it, so it too was reachable by URL and ungated.
        matchPaths: [ROUTES.usagePlanResources],
        apiPath: [ENDPOINT_PATHS.ResourcesGet],
      },
      {
        id: 'billingAdjustment',
        title: 'Billing Adjustment',
        to: ROUTES.billingAdjustment,
        // `/billing-adjustment-details/:id` is one hyphen away from nesting under the list.
        matchPaths: ['/billing-adjustment-details'],
        apiPath: [ENDPOINT_PATHS.AdminBillingAdjustmentGet],
      },
      {
        id: 'subscriptions',
        title: 'Subscriptions',
        to: ROUTES.subscriptions,
        // Unrestricted in the legacy config too — see UNRESTRICTED_NAV_IDS.
      },
      {
        id: 'invoiceMgmt',
        title: 'Invoice Mgmt',
        to: ROUTES.invoices,
        // Unrestricted in the legacy config too — see UNRESTRICTED_NAV_IDS.
      },
    ],
  },

  {
    id: 'mls',
    title: 'MLS/Node',
    icon: BuildingsIcon,
    children: [
      {
        id: 'mlsProviders',
        title: 'MLS Providers',
        to: ROUTES.mlsProviders,
        apiPath: [ENDPOINT_PATHS.ProvidersMlsMlsServersMlsServerGet],
      },
      {
        id: 'mlsPlatform',
        title: 'MLS Platform',
        to: ROUTES.mlsPlatforms,
        apiPath: [ENDPOINT_PATHS.ProvidersPlatformsPlatformGet],
      },
      {
        id: 'mlsServerInvoice',
        title: 'MLS Server Invoice',
        to: ROUTES.mlsServerInvoice,
        apiPath: [ENDPOINT_PATHS.ProvidersMlsMlsServersMlsServerGet],
      },
      {
        id: 'mlsPaymentForecast',
        title: 'MLS Payment Forecast',
        to: ROUTES.mlsPaymentForecast,
        apiPath: [ENDPOINT_PATHS.ProvidersMlsMlsServersMlsServerGet],
      },
      {
        id: 'mlsPaymentPlan',
        title: 'MLS Payment Plan',
        to: ROUTES.mlsPaymentPlans,
        apiPath: [ENDPOINT_PATHS.MembershipPaymentPlanMlsDataGet],
      },
    ],
  },

  {
    id: 'ipMonitor',
    title: 'IP Monitor',
    icon: SearchIcon,
    children: [
      {
        id: 'blockedIps',
        title: 'Blocked IP',
        to: ROUTES.blockedIps,
        apiPath: [ENDPOINT_PATHS.AdminClientCacheManageGetIpList],
      },
      {
        id: 'whitelistIps',
        title: 'Whitelist IP',
        to: ROUTES.whitelistIps,
        apiPath: [ENDPOINT_PATHS.AdminClientCacheManageGetIpList],
      },
    ],
  },

  {
    id: 'groups',
    title: 'Group',
    icon: ProfileTickIcon,
    children: [
      {
        id: 'groupMgmt',
        title: 'Group Mgmt',
        to: ROUTES.groups,
        apiPath: [ENDPOINT_PATHS.GroupManageListGet],
      },
      {
        id: 'groupPaymentPlan',
        title: 'Group Payment Plan',
        to: ROUTES.groupPaymentPlans,
        apiPath: [ENDPOINT_PATHS.AdminPaymentPlanGpmsGet],
      },
    ],
  },

  {
    id: 'adminsMgmt',
    title: 'Admins Mgmt',
    icon: SettingsGearIcon,
    children: [
      {
        id: 'users',
        title: 'All Users',
        to: ROUTES.users,
        apiPath: [ENDPOINT_PATHS.AdminEntitiesUsersPost],
      },
      {
        id: 'staffMgmt',
        title: 'Staff Mgmt',
        to: ROUTES.staff,
        apiPath: [ENDPOINT_PATHS.AdminEntitiesStaffsPost],
      },
      {
        id: 'roles',
        title: 'Roles',
        to: ROUTES.roles,
        apiPath: [ENDPOINT_PATHS.AdminEntitiesBttTypeItemsPost],
      },
      {
        id: 'permissions',
        title: 'Permissions',
        to: ROUTES.permissions,
        apiPath: [ENDPOINT_PATHS.AdminEntitiesBttPermissionAdvantagesPost],
      },
      {
        id: 'btt',
        title: 'BTT Management',
        to: ROUTES.btt,
        apiPath: [ENDPOINT_PATHS.AdminEntitiesBttTypeItemsPost],
      },
      {
        id: 'accountDeletion',
        title: 'Account Deletion',
        to: ROUTES.accountDeletion,
        apiPath: [ENDPOINT_PATHS.AdminEntitiesStaffsPost, ENDPOINT_PATHS.AdminEntitiesUsersPost],
      },
      {
        id: 'filterTemplates',
        title: 'Filter Templates',
        to: ROUTES.filterTemplates,
        /**
         * Gated on the **users** permission, which is not what this screen administers.
         *
         * Carried verbatim from the legacy, which does the same — and there is nothing better to
         * point at: `permission.enum.ts` enumerates no path for
         * `/reso/admin/mls-config/limitations`, so every alternative would be an invented
         * permission name that `ENDPOINT_PERMISSIONS` cannot resolve, and an unmapped path denies.
         *
         * The consequence is real in both directions: someone with users access sees a screen
         * that edits MLS filters, and someone who administers filters but not users cannot reach
         * it. Raised for the permission audit (**5H.5**) rather than guessed at here.
         */
        apiPath: [ENDPOINT_PATHS.AdminEntitiesUsersPost],
      },
    ],
  },

  {
    id: 'notifications',
    title: 'Notification',
    icon: InboxLetterIcon,
    children: [
      {
        id: 'notificationList',
        // Legacy spelling was "Notifcation List". Fixed — it is a label, not a path.
        title: 'Notification List',
        to: ROUTES.notifications,
        apiPath: [ENDPOINT_PATHS.NotificationsCodesGet],
      },
      {
        id: 'notificationSettings',
        title: 'Notification Settings',
        to: ROUTES.notificationSettings,
        apiPath: [ENDPOINT_PATHS.NotificationsSettingsListGet],
      },
      {
        id: 'notificationVariables',
        title: 'Variables',
        to: ROUTES.notificationVariables,
        apiPath: [ENDPOINT_PATHS.NotificationsVariablesGet],
      },
      {
        id: 'microserviceTemplates',
        title: 'Microservice Templates',
        to: ROUTES.microserviceTemplates,
        apiPath: [ENDPOINT_PATHS.NotificationsApplicationsMicroservicesTemplatesGet],
      },
      {
        id: 'notificationApplications',
        title: 'Applications',
        to: ROUTES.notificationApplications,
        apiPath: [ENDPOINT_PATHS.NotificationsApplicationsGet],
      },
    ],
  },

  {
    // One screen, so a link of its own rather than a group holding a single child.
    id: 'multiLanguage',
    title: 'Multi Language',
    icon: MessagesIcon,
    to: ROUTES.multiLanguage,
    apiPath: [ENDPOINT_PATHS.MultiLanguageDictionariesGet],
  },

  {
    // Signed Agreements is the one agreement screen in the sidebar, so it is a link of its own
    // rather than a group. Agreement Templates is still routed, just not listed here.
    id: 'signedAgreements',
    title: 'Signed Agreements',
    icon: DocumentsIcon,
    to: ROUTES.signedAgreements,
    apiPath: [ENDPOINT_PATHS.AgreementSignedGet],
  },

  {
    id: 'gdxPartner',
    title: 'GDX Partner',
    icon: ChartSquareIcon,
    to: ROUTES.gdxPartner,
    // Unrestricted in the legacy config too — see UNRESTRICTED_NAV_IDS.
  },
];

/**
 * Nav entries that deliberately carry no permission requirement.
 *
 * **All three are unrestricted in the legacy app today**, so showing them to everyone is
 * behaviour parity, not a regression introduced here:
 *
 * - `subscriptions` — the legacy entry's `apiPath` is commented out, and what it pointed at was
 *   `ERoute.SubscriptionList` (a route path) rather than an endpoint path, so it would never
 *   have matched anything even uncommented.
 * - `invoiceMgmt` — the legacy entry has no `apiPath` at all.
 * - `gdxPartner` — a top-level link with no `apiPath`.
 *
 * Inventing a permission for them here would be worse than leaving them open: a wrong guess
 * hides a working screen from the staff who need it, and the backend enforces the real rule
 * either way. **This list is a question for the product owner, not a settled answer** — see
 * `docs/04-legacy-inventory.md` §5. Removing an id from it should come with the endpoint path
 * that replaces it.
 */
export const UNRESTRICTED_NAV_IDS = ['subscriptions', 'invoiceMgmt', 'gdxPartner'] as const;
